/**
 * The one way for a Next route handler to name a download: `Content-Disposition`
 * for a file name in any script (INPUT-07, L-09).
 *
 * A Web `Response` refuses any header character above U+00FF ("Cannot convert
 * argument to a ByteString"), so a raw `filename="فاتورة.pdf"` turns the
 * download into a 500. RFC 6266 answer: an ASCII `filename` for old clients,
 * plus `filename*=UTF-8''…` (RFC 5987), which every current browser prefers.
 *
 * Same contract as the API's `apps/api/src/common/http/content-disposition.ts`;
 * a plain ASCII name comes back exactly as a hand-built header would
 * (`attachment; filename="x.csv"`), and `filename*` is added only when the
 * ASCII form would lose something.
 */

export type DispositionType = 'attachment' | 'inline'

// C0/C1 controls and DEL: never meaningful in a file name, and CR/LF would
// split the header if they ever reached it.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g
// Bidi embeddings, overrides and isolates: "invoice<RLO>fdp.exe" displays as
// "invoiceexe.pdf". ZWNJ/ZWJ (U+200C/D) stay: Persian and Indic names need them.
const BIDI_CONTROLS = /[\u202a-\u202e\u2066-\u2069]/g
// A high surrogate without its low half, or a low half without its high one,
// which `encodeURIComponent` would throw on (left by code-unit truncation).
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/g
const ASCII_EXTENSION = /\.[A-Za-z0-9]{1,10}$/
const PERCENT_ESCAPE = /%[0-9A-Fa-f]{2}/

export function contentDisposition(
  filename: string,
  disposition: DispositionType = 'attachment',
): string {
  const name = cleanFilename(filename)
  const fallback = asciiFallback(name)
  let header = `${disposition}; filename="${fallback}"`
  // A literal "%41" in a plain name is decoded by some clients; the extended
  // form carries it unambiguously.
  if (fallback !== name || PERCENT_ESCAPE.test(name)) {
    header += `; filename*=UTF-8''${encodeRfc5987(name)}`
  }
  return header
}

function cleanFilename(filename: string): string {
  const cleaned = String(filename ?? '')
    .replace(LONE_SURROGATE, '\ufffd')
    .replace(CONTROL_CHARS, '')
    .replace(BIDI_CONTROLS, '')
    .replace(/[\\/]/g, '_')
    .trim()
  return cleaned || 'download'
}

/** Printable ASCII only, no quote or backslash; the extension survives. */
function asciiFallback(name: string): string {
  const extension = ASCII_EXTENSION.exec(name)?.[0] ?? ''
  const stem = name.slice(0, name.length - extension.length)
  const asciiStem = stem.replace(/[^\x20-\x7e]+/g, '_').replace(/["\\]/g, '_')
  // A name with no ASCII letter or digit left ("______") says nothing.
  return (/[A-Za-z0-9]/.test(asciiStem) ? asciiStem : 'download') + extension
}

/** RFC 5987 ext-value: `encodeURIComponent` leaves ' ( ) * bare; attr-char does not allow them. */
function encodeRfc5987(value: string): string {
  return encodeURIComponent(value).replace(
    /['()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  )
}

/**
 * The file name a `Content-Disposition` header gives, or null: the RFC 5987
 * `filename*` (a name in any script) when it decodes, else the plain
 * `filename`. The reading side of `contentDisposition`, for the browser code
 * that saves a downloaded blob (phishing, ER and portal certificate downloads).
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  const encoded = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i)
  if (encoded) {
    try {
      const name = decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''))
      if (name) return name
    } catch {
      // fall through to the plain form
    }
  }
  const plain = header.match(/filename\s*=\s*"?([^";]+)"?/i)
  return plain ? plain[1].trim() || null : null
}
